import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  CampaignTargetsProps,
  GetCampaignProspectsResponse,
  GetCampaignResponse,
} from '@dreamai/types';
import api from '../../../../api';
import { createProspectCanvasAsset } from './prospectCanvasHelper';
import ItemList from 'components/common/ItemList';
import { ItemListItem } from 'components/common/ItemList';
import './campaign-targets.scss';

import { ReactComponent as FlipBackIcon } from '../../../../icons/flip-back.svg';
import { ReactComponent as HourglassIcon } from '../../../../icons/hourglass.svg';
import { ReactComponent as SendIcon } from '../../../../icons/send.svg';
import ErrorState from 'components/common/ErrorState';
import { useDependencyUpdates } from '../../../../providers';

interface CampaignTargetsComponentProps extends CampaignTargetsProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
  isInboxView?: boolean;
  onInboxViewComponentViewClick?: (componentId: string) => void;
}

interface ProspectCache {
  [key: string]: {
    data: GetCampaignProspectsResponse;
    timestamp: number;
  };
}

interface PaginationState {
  currentPage: number;
  hasNext: boolean;
  hasPrev: boolean;
  nextCursor: string | null;
  cursorStack: (string | null)[]; // Stack of cursors for each page: [page1_cursor, page2_cursor, page3_cursor, ...]
  loading: boolean;
}

const CampaignTargets: React.FC<CampaignTargetsComponentProps> = ({
  campaignId,
  threadId,
  updateThreadViewVisualDescription,
  isInboxView,
  onInboxViewComponentViewClick,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [prospects, setProspects] = useState<
    GetCampaignProspectsResponse['prospects']
  >([]);
  const [campaignName, setCampaignName] = useState<string>('');
  const [totalProspects, setTotalProspects] = useState<number>(0);
  const [contactedProspects, setContactedProspects] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    hasNext: false,
    hasPrev: false,
    nextCursor: null,
    cursorStack: [null], // Page 1 uses null cursor
    loading: false,
  });

  // Cache for search results and pagination data
  const cacheRef = useRef<ProspectCache>({});
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  const getCacheKey = (query: string, cursor?: string) => {
    return query ? `search:${query}` : `pagination:${cursor || 'initial'}`;
  };

  const getCachedData = (cacheKey: string) => {
    const cached = cacheRef.current[cacheKey];
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    return null;
  };

  const setCachedData = (
    cacheKey: string,
    data: GetCampaignProspectsResponse,
  ) => {
    cacheRef.current[cacheKey] = {
      data,
      timestamp: Date.now(),
    };
  };

  const fetchCampaignDetails = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get(`/campaigns/${campaignId}`);
      const data: GetCampaignResponse = response.data;

      if (data.success && data.campaign) {
        setCampaignName(data.campaign.name);
      }
    } catch (err) {
      console.error('Failed to fetch campaign details:', err);
      // Don't set error state for campaign details failure, just log it
    }
  }, [campaignId, getAccessTokenSilently]);

  const fetchProspects = useCallback(
    async (query?: string, cursor?: string, limit: number = 50) => {
      const cacheKey = getCacheKey(query || '', cursor);
      const cachedData = getCachedData(cacheKey);

      if (cachedData) {
        return cachedData;
      }

      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const params = new URLSearchParams();
      if (query) params.append('query', query);
      if (cursor) params.append('cursor', cursor);
      params.append('limit', limit.toString());

      const response = await api.get(
        `/campaigns/${campaignId}/prospects?${params}`,
      );
      const data: GetCampaignProspectsResponse = response.data;

      setCachedData(cacheKey, data);
      return data;
    },
    [campaignId, getAccessTokenSilently],
  );

  const loadInitialProspects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch campaign details and prospects in parallel
      const [data] = await Promise.all([
        fetchProspects(),
        fetchCampaignDetails(),
      ]);

      setProspects(data.prospects);
      setTotalProspects(data.totalCount || 0);
      setContactedProspects(data.totalContactedCount || 0);
      setPagination({
        currentPage: 1,
        hasNext: data.pagination.hasMore,
        hasPrev: false,
        nextCursor: data.pagination.nextCursor,
        cursorStack: [null], // Page 1 uses null cursor
        loading: false,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prospects');
    } finally {
      setLoading(false);
    }
  }, [fetchProspects, fetchCampaignDetails]);

  const goToNextPage = useCallback(async () => {
    if (!pagination.hasNext || pagination.loading || searchQuery) return;

    try {
      setPagination((prev) => ({ ...prev, loading: true }));

      const cursorForNextPage = pagination.nextCursor || undefined;

      setLoading(true);
      const data = await fetchProspects(undefined, cursorForNextPage);

      setProspects(data.prospects);
      setPagination((prev) => ({
        currentPage: prev.currentPage + 1,
        hasNext: data.pagination.hasMore,
        hasPrev: true,
        nextCursor: data.pagination.nextCursor,
        // Add the cursor used to get to this new page to the stack
        cursorStack: [...prev.cursorStack, cursorForNextPage || null],
        loading: false,
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load next page');
      setPagination((prev) => ({ ...prev, loading: false }));
    } finally {
      setLoading(false);
    }
  }, [
    fetchProspects,
    pagination.nextCursor,
    pagination.hasNext,
    pagination.loading,
    searchQuery,
  ]);

  const goToPrevPage = useCallback(async () => {
    if (!pagination.hasPrev || pagination.loading || searchQuery) return;

    try {
      setPagination((prev) => ({ ...prev, loading: true }));

      // Get the cursor for the previous page (current page - 1)
      const prevCursor =
        pagination.cursorStack[pagination.currentPage - 2] || null;
      console.log('Going to previous page with cursor:', prevCursor);
      console.log('Current cursorStack:', pagination.cursorStack);

      setLoading(true);
      const data = await fetchProspects(undefined, prevCursor || undefined);
      console.log(
        'Previous page data received:',
        data.prospects.length,
        'prospects',
      );

      setProspects(data.prospects);
      setPagination((prev) => ({
        currentPage: prev.currentPage - 1,
        hasNext: true,
        hasPrev: prev.currentPage > 2,
        nextCursor: data.pagination.nextCursor,
        // Remove the last cursor from the stack (we're going back)
        cursorStack: prev.cursorStack.slice(0, -1),
        loading: false,
      }));
    } catch (err) {
      console.error('Error in goToPrevPage:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to load previous page',
      );
      setPagination((prev) => ({ ...prev, loading: false }));
    } finally {
      setLoading(false);
    }
  }, [
    fetchProspects,
    pagination.cursorStack,
    pagination.hasPrev,
    pagination.loading,
    searchQuery,
  ]);

  const searchProspects = useCallback(
    async (query: string) => {
      try {
        setLoading(true);
        setError(null);

        const data = await fetchProspects(query);
        setProspects(data.prospects);
        // Reset pagination for search results
        setPagination({
          currentPage: 1,
          hasNext: false,
          hasPrev: false,
          nextCursor: null,
          cursorStack: [null], // Reset to page 1
          loading: false,
        });
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to search prospects',
        );
      } finally {
        setLoading(false);
      }
    },
    [fetchProspects],
  );

  const handleProspectClick = useCallback(
    async (prospectId: string) => {
      if (!threadId) {
        console.error('No threadId available for creating canvas asset');
        return;
      }

      // Find the prospect to get its name for the title
      const prospect = prospects.find((p) => String(p.id) === prospectId);
      const prospectName = prospect?.name || 'Prospect';

      await createProspectCanvasAsset({
        prospectId,
        prospectName,
        threadId,
        getAccessTokenSilently,
      });
    },
    [threadId, getAccessTokenSilently, prospects],
  );

  // Load initial prospects on mount
  useEffect(() => {
    loadInitialProspects();
  }, [loadInitialProspects]);

  // Listen for campaign updates (prospect added/removed)
  const { registerCallback } = useDependencyUpdates();
  useEffect(() => {
    const unregister = registerCallback('campaigns', campaignId, (update) => {
      console.log(
        'Campaign updated, clearing cache and reloading current page...',
      );
      // Clear cache
      cacheRef.current = {};
      // Reload current page with current cursor
      const currentCursor = pagination.cursorStack[pagination.currentPage - 1];
      fetchProspects(searchQuery || undefined, currentCursor || undefined).then(
        (data) => {
          setProspects(data.prospects);
          setTotalProspects(data.totalCount);
          setContactedProspects(data.totalContactedCount);
        },
      );
    });

    return () => unregister();
  }, [
    campaignId,
    registerCallback,
    pagination.currentPage,
    pagination.cursorStack,
    searchQuery,
    fetchProspects,
  ]);

  // Handle search query changes
  useEffect(() => {
    if (searchQuery.trim()) {
      const timeoutId = setTimeout(() => {
        searchProspects(searchQuery.trim());
      }, 300); // Debounce search

      return () => clearTimeout(timeoutId);
    } else {
      // Reset to initial state when search is cleared
      loadInitialProspects();
    }
  }, [searchQuery, searchProspects, loadInitialProspects]);

  const getStateVariant = (
    state: string | null,
  ):
    | 'default'
    | 'green'
    | 'red'
    | 'blue'
    | 'yellow'
    | 'light-blue'
    | 'beige' => {
    if (!state) return 'default';

    switch (state.toLowerCase()) {
      case 'taken offline':
        return 'yellow';
      case 'replied':
        return 'green';
      case 'opened':
        return 'light-blue';
      case 'bounced':
        return 'beige';
      case 'messaged':
        return 'blue';
      default:
        return 'default';
    }
  };

  const getStateIcon = (state: string | null): React.ReactElement => {
    if (!state) return <SendIcon />;

    switch (state.toLowerCase()) {
      case 'replied':
        return <FlipBackIcon />;
      case 'waiting to send':
        return <HourglassIcon />;
      default:
        return <SendIcon />;
    }
  };

  // All hooks must be called before any conditional returns
  const prospectsToPass: ItemListItem[] = useMemo(() => {
    console.log(
      'Recomputing prospectsToPass with',
      prospects.length,
      'prospects',
    );
    return prospects.map((prospect) => {
      return {
        id: String(prospect.id),
        title: prospect.name || 'Prospect',
        description: prospect.orgName || undefined,
        subDescription: prospect.jobTitle || prospect.email || undefined,
        imagePlaceholder: prospect.orgName?.charAt(0).toUpperCase() || 'P',
        pill: {
          text: prospect.state || 'N/A',
          variant: getStateVariant(prospect.state),
        },
        icon: getStateIcon(prospect.state),
        img: prospect.orgImageUrl || undefined,
      };
    });
  }, [prospects]);

  if (loading && prospects.length === 0) {
    return <div>Loading prospects...</div>;
  }

  if (error) {
    return (
      <div className='campaign-targets-wrapper'>
        <ErrorState
          title='Unable to load campaign targets'
          subtitle='Please refresh the page or try again'
        />
      </div>
    );
  }
  return (
    <div className='campaign-targets-wrapper'>
      <div className='header'>
        <div className='header-split'>
          <h3>Target prospects</h3>
          <div className='filter-area'>
            <label>Search targets</label>
            <input
              type='text'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className='prospect-list'>
        <p className='contacted-prospects'>
          {contactedProspects}/{totalProspects} prospects contacted
        </p>
        <div className='item-list-wrapper'>
          <ItemList
            items={prospectsToPass}
            loading={loading}
            error={!!error}
            emptyHeader={`There aren't any targets in this campaign`}
            emptyText={`You can ask your Sales Pro when targets will be added.`}
            showPagination={!searchQuery}
            nextVisible={pagination.hasNext}
            prevVisible={pagination.hasPrev}
            onNext={goToNextPage}
            onPrev={goToPrevPage}
            onItemClick={handleProspectClick}
          />
        </div>
      </div>
    </div>
  );
};

export default CampaignTargets;
